import React, { useState, useEffect, useCallback } from "react";
import {
  getSimpleGetProductRankPaymentAPI,
  updateSimpleProductRankPaymentAPI,
} from "../../../../api/index";
import "./SimpleRankPlanUpgradeModal.css";

const SimpleRankPlanUpgradeModal = ({ isOpen, onClose, onPurchaseSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [subscribeByPlanId, setSubscribeByPlanId] = useState({});
  const [purchasingId, setPurchasingId] = useState(null);

  const resetSubscribeDefaults = useCallback((list) => {
    const next = {};
    for (const p of list) {
      if (p && p.subscribe_allow === true && p.id != null) {
        next[String(p.id)] = false;
      }
    }
    setSubscribeByPlanId(next);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoadError(null);
    setPlans([]);
    setLoading(true);

    getSimpleGetProductRankPaymentAPI()
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items ?? [];
        const active = items.filter((row) => row && row.status === true);
        setPlans(active);
        resetSubscribeDefaults(active);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("결제 플랜 목록 조회 실패:", err);
          setLoadError(
            err.response?.data?.msg ||
              err.message ||
              "플랜 정보를 불러오지 못했습니다.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, resetSubscribeDefaults]);

  if (!isOpen) return null;

  const handlePurchase = async (plan) => {
    const sid = plan?.id;
    if (sid === undefined || sid === null) {
      window.alert("플랜 정보가 올바르지 않습니다.");
      return;
    }

    const subscribePayload =
      plan.subscribe_allow === true
        ? Boolean(subscribeByPlanId[String(sid)])
        : false;

    const planLabel = plan.setting_name?.trim() || "선택한 플랜";
    const subNote =
      plan.subscribe_allow === true
        ? subscribePayload
          ? "\n구독 포함: 예"
          : "\n구독 포함: 아니오"
        : "";
    const ok = window.confirm(
      `「${planLabel}」을(를) 구매하시겠습니까?${subNote}\n\n※ 구매 후 취소 및 환불은 불가합니다.\n정말 진행하시겠습니까?`,
    );
    if (!ok) return;

    setPurchasingId(sid);
    try {
      await updateSimpleProductRankPaymentAPI({
        setting_id: sid,
        subscribe_allow: subscribePayload,
      });
      window.alert("구매 처리가 완료되었습니다.");
      onClose();
      onPurchaseSuccess?.();
    } catch (err) {
      console.error("플랜 구매 실패:", err);
      window.alert(
        err.response?.data?.msg ||
          err.message ||
          "구매 처리에 실패했습니다.",
      );
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <div
      className="rk-upg-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rk-upg-title"
      aria-describedby="rk-upg-desc"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="rk-upg-popup">
        <header className="rk-upg-header">
          <div className="rk-upg-header-text">
            <h2 id="rk-upg-title">플랜 업그레이드</h2>
            <p id="rk-upg-desc" className="rk-upg-subtitle">
              상품·키워드 한도를 늘릴 수 있는 플랜을 선택하세요.
            </p>
          </div>
          <button
            type="button"
            className="rk-upg-close"
            onClick={onClose}
            aria-label="닫기"
          />
        </header>
        <div className="rk-upg-body">
          {loading && (
            <div className="rk-upg-loading" aria-live="polite">
              <div className="rk-upg-spinner" aria-hidden />
              <p>플랜을 불러오는 중입니다…</p>
            </div>
          )}
          {!loading && loadError && (
            <div className="rk-upg-error" role="alert">
              <span className="rk-upg-error-icon" aria-hidden />
              {loadError}
            </div>
          )}
          {!loading && !loadError && plans.length === 0 && (
            <div className="rk-upg-empty">
              <p>표시할 플랜이 없습니다.</p>
              <p className="rk-upg-empty-hint">
                잠시 후 다시 시도하거나 고객센터로 문의해 주세요.
              </p>
            </div>
          )}
          {!loading && !loadError && plans.length > 0 && (
            <ul className="rk-upg-plan-list">
              {plans.map((plan, idx) => {
                const pid = plan.id;
                const canSubscribe = plan.subscribe_allow === true;
                const subVal = canSubscribe
                  ? Boolean(subscribeByPlanId[String(pid)])
                  : false;
                const busy = purchasingId != null;
                const priceStr =
                  plan.price != null
                    ? `${Number(plan.price).toLocaleString("ko-KR")}원`
                    : "—";

                return (
                  <li
                    key={plan._id ?? String(pid)}
                    className="rk-upg-plan-card"
                    style={{ animationDelay: `${idx * 45}ms` }}
                  >
                    <div className="rk-upg-plan-top">
                      <div className="rk-upg-plan-title-block">
                        <span className="rk-upg-plan-badge">플랜</span>
                        <h3 className="rk-upg-plan-name">
                          {plan.setting_name ?? "플랜"}
                        </h3>
                      </div>
                      <p className="rk-upg-plan-price" aria-label="가격">
                        {priceStr}
                      </p>
                    </div>

                    <dl className="rk-upg-plan-stats">
                      <div className="rk-upg-stat">
                        <dt>이용 기간</dt>
                        <dd>
                          {plan.days != null ? `${plan.days}일` : "—"}
                        </dd>
                      </div>
                      <div className="rk-upg-stat">
                        <dt>최대 상품</dt>
                        <dd>
                          {plan.limit_product != null
                            ? `${plan.limit_product}개`
                            : "—"}
                        </dd>
                      </div>
                      <div className="rk-upg-stat">
                        <dt>최대 키워드</dt>
                        <dd>
                          {plan.limit_keyword != null
                            ? `${plan.limit_keyword}개`
                            : "—"}
                        </dd>
                      </div>
                      <div className="rk-upg-stat">
                        <dt>구독</dt>
                        <dd>
                          <span
                            className={
                              canSubscribe
                                ? "rk-upg-pill rk-upg-pill-yes"
                                : "rk-upg-pill rk-upg-pill-no"
                            }
                          >
                            {canSubscribe ? "가능" : "불가"}
                          </span>
                        </dd>
                      </div>
                    </dl>

                    <div className="rk-upg-plan-actions">
                      {canSubscribe ? (
                        <div
                          className="rk-upg-subscribe"
                          role="group"
                          aria-label="구독 여부"
                        >
                          <span className="rk-upg-subscribe-label">
                            구독 포함
                          </span>
                          <div className="rk-upg-segment">
                            <label className="rk-upg-seg-item">
                              <input
                                type="radio"
                                name={`sub-${pid}`}
                                checked={subVal === false}
                                disabled={busy}
                                onChange={() =>
                                  setSubscribeByPlanId((prev) => ({
                                    ...prev,
                                    [String(pid)]: false,
                                  }))
                                }
                              />
                              <span>아니오</span>
                            </label>
                            <label className="rk-upg-seg-item">
                              <input
                                type="radio"
                                name={`sub-${pid}`}
                                checked={subVal === true}
                                disabled={busy}
                                onChange={() =>
                                  setSubscribeByPlanId((prev) => ({
                                    ...prev,
                                    [String(pid)]: true,
                                  }))
                                }
                              />
                              <span>예</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <p className="rk-upg-subscribe-note">
                          이 플랜은 구독 옵션이 없습니다.
                        </p>
                      )}
                      <button
                        type="button"
                        className="rk-upg-buy"
                        disabled={busy}
                        onClick={() => handlePurchase(plan)}
                      >
                        {purchasingId === pid ? (
                          <>
                            <span className="rk-upg-buy-spinner" aria-hidden />
                            처리 중…
                          </>
                        ) : (
                          "이 플랜으로 구매"
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleRankPlanUpgradeModal;
